import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { GroupMemberData } from '../../shared/dto/EntityRepository';
import { shrinkString } from '../../shared/util/shrinkString';
import { GroupMemberContentMode, GroupMemberStatus } from '../../shared/enum/EnumRepository';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useGroupMemberStore } from '../../shared/store/groupMemberStore';
import { useGroupMemberContentModeStore } from '../../shared/store/groupMemberContentModeStore';

interface GroupMemberInfoProps {
  data: GroupMemberData;
}

const GroupMemberInfo: React.FC<GroupMemberInfoProps> = ({ data }) => {
  const { isEditMode } = useEditMode();
  const focusedGroupMemberId = useGroupMemberStore((s) => s.focusedGroupMemberId);
  const setFocusedGroupMemberId = useGroupMemberStore((s) => s.setFocusedGroupMemberId);
  const groupMemberContentMode = useGroupMemberContentModeStore((s) => s.groupMemberContentMode);
  const [isHover, setIsHover] = useState(false);
  const isActive = focusedGroupMemberId === data.id;
  const displayName = groupMemberContentMode === GroupMemberContentMode.ENG
    ? (isEditMode ? data.nameEn : data.nameEn.trim() || data.name)
    : data.name;
  const name = useMemo(() => shrinkString(displayName, 30), [displayName]);

  const handleClick = () => {
    setFocusedGroupMemberId(data.id);
    document.getElementById(`group-member-${data.id}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <GroupMemberInfoComp
      $isInactive={data.status === GroupMemberStatus.INACTIVE}
      onClick={handleClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <NameBlock $isActive={isActive} $isHover={isHover}>
        <NameText $isActive={isActive}>{name}</NameText>
      </NameBlock>
      <RoleBlock>{data.role}</RoleBlock>
    </GroupMemberInfoComp>
  );
}

export default GroupMemberInfo;

const GroupMemberInfoComp = styled.article<{ $isInactive: boolean }>`
  position: relative;
  display: flex;
  width: 94%;
  height: 40px;
  min-height: 38px;
  margin-right: 12px;
  cursor: pointer;
  opacity: ${({ $isInactive }) => ($isInactive ? 0.5 : 1)};

  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const NameBlock = styled.div<{ $isActive: boolean; $isHover: boolean }>`
  flex: 3;

  display: flex;
  align-items: center;

  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  padding-left: 4px;
  user-select: none;
`;

const NameText = styled.span<{ $isActive: boolean }>`
  text-decoration: ${({ $isActive }) => ($isActive ? 'underline' : 'none')};
`;

const RoleBlock = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Footer_01};
  user-select: none;
  white-space: nowrap;
`;
